import {
  Button,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  Textarea,
  toast,
  AutosizeTextarea,
  Tooltip,
} from "@altimateai/lego";
import { Spinner, Stack } from "@altimateai/lego";
import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useMutation } from "@tanstack/react-query";
import { getImprovedPrompt } from "@/helpers/apis";
import { LoadingState, useChatContext } from "@altimateai/ui-components/chatbotV2";
import { IconSparkles } from "@tabler/icons-react";

interface ImprovePromptButtonProps {
  userPrompt: string;
  onAccept: (improvedPrompt: string) => void;
}

const formSchema = z.object({
  userPrompt: z.string().min(1, "Prompt is required"),
});

type FormData = z.infer<typeof formSchema>;

const ImprovePromptButton = ({ userPrompt, onAccept }: ImprovePromptButtonProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [improvedPrompt, setImprovedPrompt] = useState("");
  const { loadingState } = useChatContext();

  const { register, formState, reset, getValues } = useForm<FormData>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      userPrompt: userPrompt,
    },
  });

  const { mutate: generateImprovedPrompt, isPending: isLoading } = useMutation({
    mutationFn: async (prompt: string) => {
      setImprovedPrompt(""); // Clear previous content
      return getImprovedPrompt(prompt, "research_agent");
    },
    onSuccess: (data?: { enhanced_prompt: string }) => {
      setImprovedPrompt(data?.enhanced_prompt || "");
    },
    onError: (error: Error) => {
      setImprovedPrompt("");
      toast({
        title: "Error generating improved prompt",
        description: `Failed to improve prompt: ${error.message}`,
        variant: "destructive",
      });
    },
  });

  const handleOpenModal = () => {
    setIsOpen(true);
    reset({ userPrompt });
    if (userPrompt) {
      // Generate improved prompt immediately when modal opens
      generateImprovedPrompt(userPrompt);
    }
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    setImprovedPrompt("");
  };

  const handleRegenerate = () => {
    const currentPrompt = getValues("userPrompt");
    if (currentPrompt) {
      generateImprovedPrompt(currentPrompt);
    }
  };

  const handleAccept = () => {
    onAccept(improvedPrompt);
    handleCloseModal();
  };

  return (
    <>
      <Tooltip title={"Improve Prompt"}>
        <Button
          type="button"
          variant="outline"
          color="primary"
          size="default"
          onClick={handleOpenModal}
          disabled={loadingState === LoadingState.LOADING}
          aria-label="Improve prompt"
        >
          <IconSparkles className={"al-size-4"} />
        </Button>
      </Tooltip>

      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="al-max-w-[700px] al-max-h-[90vh] al-overflow-y-auto">
          <form className="al-p-2">
            <DialogHeader>
              <DialogTitle>Improve your prompt</DialogTitle>
            </DialogHeader>

            <Stack direction="column" className="al-gap-4 al-mt-6">
              <Stack direction="column" className="al-gap-2">
                <label htmlFor="user-prompt-field" className="al-text-sm al-font-medium">
                  Your Prompt
                </label>
                <Textarea
                  id="user-prompt-field"
                  {...register("userPrompt")}
                  placeholder="Enter your prompt"
                  className="al-min-h-[120px] al-resize-y"
                />
                {formState.errors.userPrompt && (
                  <p className="al-text-sm al-text-destructive">
                    {formState.errors.userPrompt.message}
                  </p>
                )}
              </Stack>

              <Stack direction="column" className="al-gap-2">
                <label htmlFor="enhanced-prompt-field" className="al-text-sm al-font-medium">
                  <Stack direction="row" align="center" gap="sm">
                    Enhanced Prompt
                    {isLoading ? <Spinner size="sm" /> : null}
                  </Stack>
                </label>
                <AutosizeTextarea
                  id="enhanced-prompt-field"
                  value={improvedPrompt}
                  autoFocus
                  readOnly
                  placeholder={
                    isLoading ? "Generating improved prompt..." : "Improved prompt will appear here"
                  }
                  minHeight={120}
                  maxHeight={400}
                />
              </Stack>
            </Stack>

            <DialogFooter className="al-mt-6">
              <Stack direction="row" className="al-gap-2 al-w-full">
                <Button
                  type="button"
                  variant="outline"
                  className="al-flex-1"
                  onClick={handleCloseModal}
                >
                  Reject
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  className="al-flex-1"
                  onClick={handleRegenerate}
                  disabled={isLoading}
                >
                  {isLoading ? "Generating..." : "Regenerate"}
                </Button>
                <Button
                  type="button"
                  className="al-flex-1"
                  onClick={handleAccept}
                  disabled={!improvedPrompt || isLoading}
                >
                  Accept
                </Button>
              </Stack>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ImprovePromptButton;
